import { useCallback, useEffect, useRef } from 'react';

/**
 * Keep tabs in sync with selected items / active tab changes / view mode.
 */
export function useTabSync({
  activeTab,
  tabs,
  tabsLoaded,
  updateTab,
  openTab,
  selectedSprint,
  selectedTemplate,
  activeView,
  isInitialStartupComplete,
  handleTabClick,
  consumeSkipNextActiveTabEffect,
  preferences,
}) {
  // Update tab when sprint is renamed
  useEffect(() => {
    if (activeTab && selectedSprint && activeTab.targetId === selectedSprint.id) {
      if (activeTab.label !== selectedSprint.name || activeTab.icon !== selectedSprint.icon) {
        updateTab(activeTab.id, {
          label: selectedSprint.name,
          icon: selectedSprint.icon,
        });
      }
    }
  }, [activeTab, selectedSprint, updateTab]);

  // Update tab when template is renamed
  useEffect(() => {
    if (activeTab && selectedTemplate && activeTab.targetId === selectedTemplate.id) {
      if (activeTab.label !== selectedTemplate.name || activeTab.icon !== selectedTemplate.icon) {
        updateTab(activeTab.id, {
          label: selectedTemplate.name,
          icon: selectedTemplate.icon,
        });
      }
    }
  }, [activeTab, selectedTemplate, updateTab]);

  // Update tab when view is loaded or renamed
  // This handles the case where a view tab was created with placeholder data
  // (e.g., from URL navigation) and the actual view data is now available
  useEffect(() => {
    if (activeTab && activeTab.type === 'view' && activeView && activeTab.targetId === activeView.id) {
      const viewName = activeView.name || 'View';
      const viewIcon = activeView.icon || '📊';
      if (activeTab.label !== viewName || activeTab.icon !== viewIcon) {
        updateTab(activeTab.id, {
          label: viewName,
          icon: viewIcon,
        });
      }
    }
  }, [activeTab, activeView, updateTab]);

  // Navigate to the active tab's content when activeTab changes
  // This handles the case when a tab is closed and we need to navigate to the adjacent tab
  // (and openTab / setActiveTabId paths). Tab bar clicks use switchToTab + onTabClick already — skip duplicate.
  const prevActiveTabIdRef = useRef(activeTab?.id);
  useEffect(() => {
    // Only trigger navigation if the active tab changed (not on initial mount)
    // and we have a valid active tab
    if (!activeTab || !isInitialStartupComplete) return;

    const prevId = prevActiveTabIdRef.current;
    prevActiveTabIdRef.current = activeTab.id;

    // Skip if this is the same tab (just updated) or initial mount.
    // Still consume any pending skip flag (e.g. from the startup tab-highlight
    // sync) so a stale flag can't suppress a future real tab switch.
    if (prevId === activeTab.id || prevId === undefined) {
      consumeSkipNextActiveTabEffect();
      return;
    }

    if (consumeSkipNextActiveTabEffect()) return;

    handleTabClick(activeTab);
  }, [activeTab?.id, isInitialStartupComplete, handleTabClick, consumeSkipNextActiveTabEffect]);

  // Safety net: the bar must never be empty after startup. closeTab already
  // leaves a Campaigns home chip; this covers first-run and workspace reset.
  useEffect(() => {
    if (!tabsLoaded || !isInitialStartupComplete) return;
    if (tabs.length > 0) return;
    if (selectedSprint) {
      openTab({
        type: 'campaign',
        targetId: selectedSprint.id,
        label: selectedSprint.name,
        icon: selectedSprint.icon,
        viewMode: preferences.defaultView || 'flow',
      });
      return;
    }
    openTab({
      type: 'campaigns',
      targetId: null,
      label: 'Campaigns',
      icon: null,
      viewMode: null,
    });
  }, [tabsLoaded, isInitialStartupComplete, selectedSprint, tabs.length, openTab, preferences.defaultView]);

  // Update the current tab's viewMode and customViewId when the view changes in SprintDetail
  // This adds to history so back/forward can navigate between views
  // @param {Object} options - Options
  // @param {boolean} options.skipHistory - If true, don't add to history (used when navigating through history)
  const handleViewModeChange = useCallback((newViewMode, customViewId = null, options = {}) => {
    const { skipHistory = false } = options;
    if (!activeTab || !newViewMode) return;

    // If skipHistory is true, we're navigating through history - don't update the tab at all
    // The tab has already been updated by goBack/goForward
    if (skipHistory) return;

    // Tab bar switches activeTab before the campaign/template id flips. Ignore
    // writes from the still-mounted previous item so we don't stamp its view
    // (or its view id) onto the tab we just clicked.
    const ownsContent =
      (activeTab.type === 'campaign' && selectedSprint?.id === activeTab.targetId) ||
      (activeTab.type === 'template' && selectedTemplate?.id === activeTab.targetId) ||
      (activeTab.type === 'view' && activeView?.id === activeTab.targetId);
    if (!ownsContent) return;

    const viewModeChanged = newViewMode !== activeTab.viewMode;
    const customViewIdChanged = customViewId !== activeTab.customViewId;

    if (viewModeChanged || customViewIdChanged) {
      updateTab(activeTab.id, { viewMode: newViewMode, customViewId }, { addToHistory: true });
    }
  }, [activeTab, updateTab, selectedSprint?.id, selectedTemplate?.id, activeView?.id]);

  // Sync the current tab to match URL-driven navigation
  // This is called when back/forward navigation changes the URL
  const syncTabToContent = useCallback((type, targetId, label, icon, viewMode) => {
    if (activeTab) {
      updateTab(activeTab.id, {
        type,
        targetId,
        label,
        icon,
        viewMode: viewMode || activeTab.viewMode || preferences.defaultView || 'flow',
      });
    }
  }, [activeTab, updateTab, preferences.defaultView]);

  return {
    handleViewModeChange,
    syncTabToContent,
  };
}
